import React, { useEffect, useState, useRef } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { personalInfo, statsData } from "../data/mock";

const ROLES = ["Hardware Expert", "Software Specialist", "Network Engineer", "IT Professional"];

export default function Hero() {
  const [roleIdx, setRoleIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const statsRef = useRef(null);
  const [counted, setCounted] = useState(false);
  const [counts, setCounts] = useState(statsData.map(() => 0));

  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  // Role rotator
  useEffect(() => {
    const t = setInterval(() => setRoleIdx(i => (i + 1) % ROLES.length), 3000);
    return () => clearInterval(t);
  }, []);

  // Count up on scroll
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting && !counted) { setCounted(true); } }, { threshold: 0.5 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, [counted]);

  useEffect(() => {
    if (!counted) return;
    statsData.forEach((stat, i) => {
      const target = parseInt(stat.value);
      let start = 0;
      const step = () => {
        start += Math.ceil(target / 30);
        if (start >= target) start = target;
        setCounts(c => { const n = [...c]; n[i] = start; return n; });
        if (start < target) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }, [counted]);

  const nav = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", overflow: "hidden", paddingTop: 80 }}>

      {/* Background ghost text */}
      <div style={{
        position: "absolute", right: -40, top: "50%", transform: "translateY(-50%)",
        fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
        fontSize: "clamp(180px, 28vw, 400px)",
        color: "transparent", WebkitTextStroke: "1px rgba(255,255,255,0.03)",
        lineHeight: 1, userSelect: "none", pointerEvents: "none",
        letterSpacing: "-8px",
      }}>IT</div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px", position: "relative", zIndex: 1, width: "100%" }}>

        {/* Status badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px",
          border: "1px solid rgba(0,229,255,0.2)",
          borderRadius: 100, marginBottom: 40,
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(20px)",
          transition: "opacity 0.6s ease, transform 0.6s ease",
        }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#00e5ff", boxShadow: "0 0 8px #00e5ff", animation: "float 2s ease infinite" }} />
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6a6a88", letterSpacing: 1 }}>AVAILABLE FOR PROJECTS · MEDAN</span>
        </div>

        {/* Name — Editorial style */}
        <div style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(40px)",
          transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
          marginBottom: 8,
        }}>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(56px, 10vw, 130px)",
            fontWeight: 700, lineHeight: 0.95,
            margin: 0, letterSpacing: "-4px",
            color: "#f2f2ff",
          }}>
            {personalInfo.name.split(" ").map((word, i) => (
              <span key={i} style={{ display: "block" }}>
                {i === 1 ? (
                  <span style={{
                    background: "linear-gradient(135deg, #00e5ff, #9b00ff, #ff0099, #ffb700, #00e5ff)",
                    backgroundSize: "400% 400%",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    animation: "holo-shift 5s ease infinite",
                  }}>{word}</span>
                ) : word}
              </span>
            ))}
          </h1>
        </div>

        {/* Horizontal rule with role */}
        <div style={{
          display: "flex", alignItems: "center", gap: 20, marginBottom: 40,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.7s ease 0.2s",
        }}>
          <div style={{ height: 1, width: 60, background: "linear-gradient(90deg, #00e5ff, #9b00ff)", flexShrink: 0 }} />
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "clamp(13px, 2vw, 16px)", color: "#6a6a88", letterSpacing: 2 }}>
            <span key={roleIdx} style={{ animation: "fadeIn 0.4s ease" }}>{ROLES[roleIdx].toUpperCase()}</span>
          </div>
        </div>

        {/* Tagline */}
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "clamp(15px, 1.8vw, 18px)",
          color: "#6a6a88", lineHeight: 1.8,
          maxWidth: 480, margin: "0 0 48px",
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(20px)",
          transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s",
        }}>{personalInfo.tagline}</p>

        {/* CTAs */}
        <div style={{
          display: "flex", gap: 16, flexWrap: "wrap",
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(20px)",
          transition: "opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s",
          marginBottom: 80,
        }}>
          <button onClick={() => nav("portfolio")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "14px 32px", borderRadius: 4,
              background: "linear-gradient(135deg, #00e5ff, #9b00ff)",
              color: "#07070f", fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700, fontSize: 14, border: "none", cursor: "none",
              letterSpacing: 1, transition: "opacity 0.2s, transform 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}
          >
            LIHAT PORTFOLIO <ArrowRight size={16} />
          </button>
          <button onClick={() => nav("contact")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "14px 32px", borderRadius: 4,
              background: "transparent",
              color: "#f2f2ff", fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600, fontSize: 14, cursor: "none",
              letterSpacing: 1,
              border: "1px solid rgba(255,255,255,0.12)",
              transition: "border-color 0.2s, transform 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(0,229,255,0.4)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.transform = "none"; }}
          >
            HUBUNGI SAYA
          </button>
        </div>

        {/* Stats */}
        <div ref={statsRef} style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
          gap: 0, borderTop: "1px solid rgba(255,255,255,0.07)",
          paddingTop: 40,
        }} className="stats-row">
          {statsData.map((stat, i) => (
            <div key={i} style={{ paddingRight: 32 }}>
              <div style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "clamp(36px, 5vw, 60px)",
                fontWeight: 700, lineHeight: 1, marginBottom: 6,
                background: "linear-gradient(135deg, #00e5ff, #9b00ff, #ff0099, #ffb700, #00e5ff)",
                backgroundSize: "400% 400%",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: `holo-shift ${4 + i * 0.5}s ease infinite`,
              }}>
                {counts[i]}{stat.value.replace(/[0-9]/g,"")}
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6a6a88", letterSpacing: 0.5 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <button onClick={() => nav("about")}
        style={{
          position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
          background: "none", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "50%", width: 44, height: 44,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "none", color: "#6a6a88",
          animation: "float 2.5s ease infinite",
          transition: "border-color 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(0,229,255,0.4)"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"}
      >
        <ArrowDown size={18} />
      </button>
    </div>
  );
}
